import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { BreadcrumbNav } from '@/components/base/BreadcrumbNav';
import { animeList } from '@/mocks/anime';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedEp, setSelectedEp] = useState<number | null>(null);

  const anime = animeList.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    if (anime) {
      document.title = `${anime.title} - 奶泡动漫高清在线观看 | ${anime.category} ${anime.subCategory}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          `${anime.title}高清在线观看，评分${anime.rating}分，共${anime.episodes}集。${anime.description.substring(0, 120)}`
        );
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `${window.location.origin}/article/${anime.id}`);
      }
    }
  }, [anime]);

  if (!anime) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-10 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-error-warning-line text-2xl text-primary-500"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">
              番剧未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">你访问的番剧不存在或已下架</p>
            <button
              onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); navigate('/'); }}
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedAnime = animeList
    .filter(
      (a) =>
        a.id !== anime.id &&
        (a.category === anime.category ||
          a.subCategory === anime.subCategory ||
          a.tags.some((t) => anime.tags.includes(t)))
    )
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${anime.title} - 奶泡动漫`,
        description: anime.description,
        image: anime.coverImage,
        datePublished: anime.episodesList[0]?.publishDate,
        author: { '@type': 'Organization', name: '奶泡动漫' },
        publisher: { '@type': 'Organization', name: '奶泡动漫' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin + '/' },
          { '@type': 'ListItem', position: 2, name: anime.category, item: `${window.location.origin}/category/${anime.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: anime.title },
        ],
      },
    ],
  };

  const handleAnimeClick = (targetId: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/article/${targetId}`);
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <BreadcrumbNav
            items={[
              { label: '首页', href: '/' },
              { label: anime.category, href: `/category/${anime.categorySlug}` },
              { label: anime.title },
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-4">
                  <img
                    src={anime.coverImage}
                    alt={anime.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                    {anime.rating}分
                  </span>
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                    {anime.category}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    {anime.subCategory}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs font-medium whitespace-nowrap">
                    {anime.status}
                  </span>
                </div>

                <div className="mt-6 p-4 rounded-lg bg-background-100 space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-500">总集数</span>
                    <span className="text-foreground-900 font-medium">{anime.episodes}集</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-500">放送年份</span>
                    <span className="text-foreground-900 font-medium">{anime.year}年</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-500">放送季节</span>
                    <span className="text-foreground-900 font-medium">{anime.season}季</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-500">更新日期</span>
                    <span className="text-foreground-900 font-medium">每周{anime.updateDay}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-foreground-500">播放量</span>
                    <span className="text-foreground-900 font-medium">{(anime.views / 10000).toFixed(0)}万</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {anime.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {anime.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-600 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-sm max-w-none mb-8">
                <h2 className="text-lg font-semibold text-foreground-900 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{anime.description}</p>
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground-900 mb-4">分集列表</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {anime.episodesList.map((ep) => (
                    <div
                      key={ep.id}
                      onClick={() => setSelectedEp(selectedEp === ep.number ? null : ep.number)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedEp === ep.number
                          ? 'border-primary-300 bg-primary-50'
                          : 'border-background-200/70 bg-background-50 hover:border-primary-200 hover:bg-primary-50/50'
                      }`}
                    >
                      <div className="text-xs font-medium text-foreground-900 mb-1">
                        第{ep.number}集
                      </div>
                      <div className="text-xs text-foreground-500 truncate">{ep.title}</div>
                      {selectedEp === ep.number && (
                        <div className="mt-2 pt-2 border-t border-background-200">
                          <div className="relative aspect-video rounded overflow-hidden bg-background-300 mb-2">
                            <img
                              src={ep.thumbnail}
                              alt={`${anime.title} 第${ep.number}集`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500">
                                <i className="ri-play-fill text-white text-xl"></i>
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-foreground-500">
                            时长: {ep.duration} · 发布: {ep.publishDate}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {relatedAnime.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-foreground-900 mb-4">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {relatedAnime.map((rel) => (
                      <div
                        key={rel.id}
                        onClick={() => handleAnimeClick(rel.id)}
                        className="group cursor-pointer"
                      >
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2">
                          <img
                            src={rel.coverImage}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                          />
                        </div>
                        <h3 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {rel.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5">{rel.rating}分</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}